import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Avatar,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import api from '../../services/api';

export default function LessonForm({
  open,
  lesson = null,
  module = null,
  onSave,
  onClose,
  // loading = false
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '', // URL de vídeo (YouTube) ou conteúdo texto
    position: 1,
    durationSeconds: 0,
    pdfFile: null,
  });

  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingPdf, setExistingPdf] = useState(null); // PDF já existente

  const submittingRef = useRef(false);

  useEffect(() => {
    if (lesson && open) {
      setFormData({
        title: lesson.title || '',
        description: lesson.description || '',
        content: lesson.content || '',
        position: lesson.position || 1,
        durationSeconds: lesson.durationSeconds || 0,
        pdfFile: null, // Sempre null ao editar, arquivo novo
      });

      // Buscar PDF já existente na aula
      if (lesson.materials && lesson.materials.length > 0) {
        const pdfMaterial = lesson.materials.find(m => m.type === 'PDF');
        setExistingPdf(pdfMaterial || null);
      } else {
        setExistingPdf(null);
      }

      setError('');
    } else if (open) {
      setFormData({
        title: '',
        description: '',
        content: '',
        position: (module?.lessons?.length || 0) + 1,
        durationSeconds: 0,
        pdfFile: null,
      });
      setExistingPdf(null);
      setError('');
    }
  }, [lesson, open, module]);

  const handleChange = e => {
    const { name, value, type, checked, files } = e.target;

    if (name === 'pdfFile') {
      setFormData(prev => ({
        ...prev,
        pdfFile: files?.[0] || null,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current || localLoading) return; // Prevent double submission

    if (!formData.title.trim()) {
      setError('Por favor, preencha o título da aula');
      return;
    }

    submittingRef.current = true;
    setLocalLoading(true);
    setError('');

    try {
      const dataToSend = {
        title: formData.title,
        description: formData.description,
        content: formData.content,
        position: formData.position,
        durationSeconds: formData.durationSeconds ? parseInt(formData.durationSeconds, 10) : null,
      };

      // Se houver arquivo PDF, enviar como FormData
      if (formData.pdfFile) {
        const sendFormData = new FormData();
        sendFormData.append('title', formData.title);
        sendFormData.append('description', formData.description);
        sendFormData.append('content', formData.content);
        sendFormData.append('position', formData.position);

        // Só adiciona durationSeconds se tiver valor
        const duration = formData.durationSeconds ? parseInt(formData.durationSeconds, 10) : null;
        if (duration !== null) {
          sendFormData.append('durationSeconds', duration);
        }

        sendFormData.append('file', formData.pdfFile);

        if (lesson?.id) {
          await api.put(`/lessons/${lesson.id}`, sendFormData);
        } else {
          sendFormData.append('moduleId', module.id);
          await api.post('/lessons', sendFormData);
        }
      } else if (lesson?.id) {
        // Sem arquivo, enviar como JSON
        await api.put(`/lessons/${lesson.id}`, dataToSend);
      } else {
        dataToSend.moduleId = module.id;
        await api.post('/lessons', dataToSend);
      }

      onSave(dataToSend);
    } catch (err) {
      setError(`Erro ao salvar aula: ${err.response?.data?.message || err.message}`);
    } finally {
      setLocalLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          height: '80vh',
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          fontWeight: 600,
          fontSize: '1.25rem',
          borderRadius: '16px 16px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
            <MenuBookIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            {lesson ? 'Editar Aula' : 'Nova Aula'}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          backgroundColor: 'rgba(15, 23, 42, 0.8)',
          px: 3,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 2,
          borderTop: '1px solid rgba(59, 130, 246, 0.1)',
          minHeight: '50vh',
          overflowY: 'auto',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* TÍTULO */}
        <TextField
          autoFocus
          sx={{
            width: '100%',
            maxWidth: 450,
            mt: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              borderRadius: '12px',
              '& input': { color: '#ffffff' },
              '&:hover fieldset': { borderColor: '#3b82f6' },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: '2px',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#3b82f6',
            },
          }}
          label="Título da Aula *"
          name="title"
          variant="outlined"
          value={formData.title}
          onChange={handleChange}
        />

        {/* DESCRIÇÃO */}
        <TextField
          sx={{
            width: '100%',
            maxWidth: 450,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              borderRadius: '12px',
              '& textarea': { color: '#ffffff' },
              '&:hover fieldset': { borderColor: '#3b82f6' },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: '2px',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#3b82f6',
            },
          }}
          label="Descrição"
          name="description"
          multiline
          rows={2}
          variant="outlined"
          value={formData.description}
          onChange={handleChange}
        />

        {/* CONTEÚDO (URL de vídeo ou texto) */}
        <TextField
          sx={{
            width: '100%',
            maxWidth: 450,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              borderRadius: '12px',
              '& textarea': { color: '#ffffff' },
              '&:hover fieldset': { borderColor: '#3b82f6' },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: '2px',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#3b82f6',
            },
          }}
          label="Conteúdo (URL YouTube, links ou texto)"
          name="content"
          multiline
          rows={3}
          variant="outlined"
          placeholder="https://www.youtube.com/watch?v=... ou conteúdo em texto"
          value={formData.content}
          onChange={handleChange}
        />

        {/* UPLOAD PDF */}
        <Box sx={{ width: '100%', maxWidth: 450 }}>
          <Typography
            sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1, fontSize: '0.9rem', fontWeight: 600 }}
          >
            📎 PDF da Aula
          </Typography>

          {/* Mostrar PDF já existente */}
          {existingPdf && !formData.pdfFile && (
            <Box
              sx={{
                p: 1.5,
                mb: 1,
                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <span style={{ color: '#06b6d4', fontSize: '1.2rem' }}>✓</span>
              <Typography sx={{ color: '#06b6d4', fontSize: '0.9rem', flex: 1 }}>
                {existingPdf.fileName || existingPdf.title || 'PDF'}
              </Typography>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem' }}>
                (novo arquivo substituirá)
              </Typography>
            </Box>
          )}

          {/* Input de arquivo */}
          <input
            type="file"
            name="pdfFile"
            accept=".pdf"
            onChange={handleChange}
            style={{
              display: 'block',
              padding: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px dashed rgba(59, 130, 246, 0.3)',
              borderRadius: '6px',
              color: '#ffffff',
              width: '100%',
              boxSizing: 'border-box',
              cursor: 'pointer',
            }}
          />

          {/* Arquivo selecionado */}
          {formData.pdfFile && (
            <Box
              sx={{
                mt: 1,
                p: 1,
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <span style={{ color: '#86efac', fontSize: '1rem' }}>✓</span>
              <Typography sx={{ color: '#86efac', fontSize: '0.85rem' }}>
                {formData.pdfFile.name}
              </Typography>
            </Box>
          )}
        </Box>

        {/* POSIÇÃO E DURAÇÃO */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 450,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
          }}
        >
          <TextField
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#ffffff',
                borderRadius: '12px',
                '& input': { color: '#ffffff' },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiFormHelperText-root': {
                color: 'rgba(255, 255, 255, 0.6)',
              },
            }}
            label="Posição"
            name="position"
            type="number"
            inputProps={{ min: 1 }}
            variant="outlined"
            value={formData.position}
            onChange={handleChange}
            disabled
            helperText="Reordene usando drag-and-drop"
          />

          <TextField
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                borderRadius: '12px',
                '& input': { color: '#ffffff' },
                '&:hover fieldset': { borderColor: '#3b82f6' },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#3b82f6',
              },
            }}
            label="Duração (segundos)"
            name="durationSeconds"
            type="number"
            inputProps={{ min: 0 }}
            variant="outlined"
            value={formData.durationSeconds}
            onChange={handleChange}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '1rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: '#1e293b',
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          disabled={localLoading}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&:disabled': {
              color: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={localLoading || !formData.title.trim()}
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            borderRadius: '6px',
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #0891b2 100%)',
            },
            '&:disabled': {
              background: '#475569',
              color: '#94a3b8',
            },
          }}
        >
          {localLoading ? 'Salvando...' : 'Salvar Aula'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
